import React, { useState, useMemo } from 'react';
import { BarChart2, Grid, Table as TableIcon, Layers, TrendingUp, Landmark } from 'lucide-react';
import {
  ModalShell,
  InstitutionalKpiCard,
  SegmentedTabs,
  FilterPillGroup,
  InstitutionalChartHud,
} from '../common/ui';
import {
  MfLatestSnapshot,
  MfHistoryPayload,
  MfMonthlyPayload,
  MfMonthlyItem,
} from '../../types';
import { formatCr, formatLakhCr } from '../../services/mutualFundFlowService';
import '../../styles/fiiDii.css';
import '../../styles/mutualFund.css';

export interface MutualFundFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  latest: MfLatestSnapshot | null;
  history: MfHistoryPayload | null;
  monthly: MfMonthlyPayload | null;
  loadingDetails: boolean;
  onRefresh: () => Promise<void>;
  initialSubTab?: 'chart' | 'matrix' | 'category' | 'daily';
  onSwitchToFiiDii?: () => void;
}

type MfSubTab = 'chart' | 'matrix' | 'category' | 'daily';
type ChartMetric = 'both' | 'sip' | 'equity' | 'aum';
type MatrixMetric = 'sip' | 'equity' | 'aum';
type ChartRange = 'all' | '3y' | '1y' | 'recent';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const openMutualFundModal = (tab?: MfSubTab) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('open-mutual-fund-modal', { detail: { tab } }));
  }
};

export const MutualFundFlowModal: React.FC<MutualFundFlowModalProps> = ({
  isOpen,
  onClose,
  latest,
  history,
  monthly,
  loadingDetails,
  onRefresh,
  initialSubTab = 'chart',
  onSwitchToFiiDii,
}) => {
  const [activeTab, setActiveTab] = useState<MfSubTab>(initialSubTab);
  const [chartMetric, setChartMetric] = useState<ChartMetric>('both');
  const [matrixMetric, setMatrixMetric] = useState<MatrixMetric>('sip');
  const [chartRange, setChartRange] = useState<ChartRange>('all');
  const [hoveredMonth, setHoveredMonth] = useState<MfMonthlyItem | null>(null);
  const [selectedDailyMonth, setSelectedDailyMonth] = useState<string>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);


  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Available monthly items
  const monthlyItems = useMemo(() => {
    return monthly?.monthlyData || [];
  }, [monthly?.monthlyData]);

  // Filtered monthly items based on chart range
  const filteredChartMonths = useMemo(() => {
    if (monthlyItems.length === 0) return [];
    if (chartRange === '1y') return monthlyItems.slice(-12);
    if (chartRange === '3y') return monthlyItems.slice(-36);
    if (chartRange === 'recent') return monthlyItems.filter((m) => m.year >= 2024);
    return monthlyItems; // 'all' (5+ years, 70+ months)
  }, [monthlyItems, chartRange]);

  // Selected or active hover month item
  const activeHoverItem = useMemo(() => {
    if (hoveredMonth) return hoveredMonth;
    if (filteredChartMonths.length > 0) return filteredChartMonths[filteredChartMonths.length - 1];
    return null;
  }, [hoveredMonth, filteredChartMonths]);

  // Check if negative equity outflows exist in the selected range
  const hasNegativeEquity = useMemo(() => {
    if (chartMetric === 'sip' || chartMetric === 'aum') return false;
    return filteredChartMonths.some((m) => m.netEquityInflowCr < 0);
  }, [filteredChartMonths, chartMetric]);

  // Max positive value for upper zone
  const maxPosVal = useMemo(() => {
    if (filteredChartMonths.length === 0) return 40000;
    if (chartMetric === 'sip') {
      const maxVal = Math.max(...filteredChartMonths.map((m) => m.sipInflowCr));
      return maxVal > 0 ? maxVal * 1.12 : 40000;
    }
    if (chartMetric === 'aum') {
      const maxVal = Math.max(...filteredChartMonths.map((m) => m.totalIndustryAumLakhCr));
      return maxVal > 0 ? maxVal * 1.08 : 80;
    }
    if (chartMetric === 'equity') {
      const maxVal = Math.max(1000, ...filteredChartMonths.map((m) => Math.max(0, m.netEquityInflowCr)));
      return maxVal * 1.12;
    }
    // 'both': either SIP or Net Equity
    const maxVal = Math.max(
      1000,
      ...filteredChartMonths.map((m) => Math.max(m.sipInflowCr, Math.max(0, m.netEquityInflowCr)))
    );
    return maxVal * 1.12;
  }, [filteredChartMonths, chartMetric]);

  // Max negative value for lower zone
  const maxNegVal = useMemo(() => {
    if (!hasNegativeEquity || filteredChartMonths.length === 0) return 0;
    const maxNeg = Math.max(1000, ...filteredChartMonths.map((m) => Math.max(0, -m.netEquityInflowCr)));
    return maxNeg * 1.2;
  }, [filteredChartMonths, hasNegativeEquity]);

  // Proportions: Allocate 74% to upper positive zone and 26% to lower negative zone when negative outflows exist
  const posZonePct = hasNegativeEquity ? 74 : 100;
  const negZonePct = hasNegativeEquity ? 26 : 0;

  // Y-axis tick labels
  const yAxisTopLabel = useMemo(() => {
    if (chartMetric === 'aum') return `₹${Math.round(maxPosVal)}L Cr`;
    return `+₹${Math.round(maxPosVal / 1000)}k Cr`;
  }, [chartMetric, maxPosVal]);

  const yAxisMidLabel = useMemo(() => {
    if (chartMetric === 'aum') return `₹${Math.round(maxPosVal / 2)}L Cr`;
    return `+₹${Math.round(maxPosVal / 2000)}k Cr`;
  }, [chartMetric, maxPosVal]);

  const yAxisNegLabel = useMemo(() => {
    if (!hasNegativeEquity || maxNegVal === 0) return '';
    return `-₹${Math.round(maxNegVal / 1000)}k Cr`;
  }, [hasNegativeEquity, maxNegVal]);

  // X-axis label helper to avoid duplicate single-letter labels and display clean year/month marks
  const getChartXAxisLabel = (item: (typeof filteredChartMonths)[0], idx: number, total: number) => {
    const isLast = idx === total - 1;
    const yearShort = `'${String(item.year).slice(2)}`;

    if (chartRange === '1y') {
      if (idx % 2 === 0 || isLast) {
        return `${item.monthName} ${yearShort}`;
      }
      return '';
    }

    if (chartRange === '3y' || chartRange === 'recent') {
      if (item.month === 1) return `Jan ${yearShort}`;
      if (item.month === 7 && !isLast && total - 1 - idx >= 2) return `Jul ${yearShort}`;
      if (isLast) return `${item.monthName} ${yearShort}`;
      return '';
    }

    // 'all' (80+ months, 2020–2026):
    if (item.month === 1) {
      return String(item.year);
    }
    if (isLast) {
      return `${item.monthName} ${yearShort}`;
    }
    return '';
  };

  // Total 5-Year Cumulative figures
  const summaryKpis = useMemo(() => {
    if (monthlyItems.length === 0) {
      return {
        latestSip: latest?.latestMonthSipCr ?? 32490,
        latestEquity: latest?.latestMonthEquityInflowCr ?? 38294,
        latestAum: latest?.latestIndustryAumLakhCr ?? 82.64,
        total5YrSip: 1184320,
        total5YrEquity: 1042500,
        latestAccounts: 2862,
      };
    }

    const latestM = monthlyItems[monthlyItems.length - 1];
    const totalSip = monthlyItems.reduce((acc, m) => acc + m.sipInflowCr, 0);
    const totalEquity = monthlyItems.reduce((acc, m) => acc + m.netEquityInflowCr, 0);

    return {
      latestSip: latestM.sipInflowCr,
      latestEquity: latestM.netEquityInflowCr,
      latestAum: latestM.totalIndustryAumLakhCr,
      total5YrSip: totalSip,
      total5YrEquity: totalEquity,
      latestAccounts: latestM.activeSipAccountsLakh ?? latestM.sipAccountsLakh ?? 2862,
    };
  }, [monthlyItems, latest]);

  // Matrix data (grouped by year)
  const matrixData = useMemo(() => {
    if (monthlyItems.length === 0) return [];
    const years = Array.from(new Set(monthlyItems.map((m) => m.year))).sort((a, b) => b - a);

    return years.map((year) => {
      const yearMonths = monthlyItems.filter((m) => m.year === year);
      const monthsMap: Record<number, number> = {};
      yearMonths.forEach((m) => {
        monthsMap[m.month] =
          matrixMetric === 'sip'
            ? m.sipInflowCr
            : matrixMetric === 'equity'
            ? m.netEquityInflowCr
            : m.totalIndustryAumLakhCr;
      });

      const totalVal =
        matrixMetric === 'aum'
          ? yearMonths[yearMonths.length - 1]?.totalIndustryAumLakhCr ?? 0
          : Object.values(monthsMap).reduce((acc, v) => acc + (v ?? 0), 0);

      return {
        year,
        months: monthsMap,
        total: totalVal,
        count: yearMonths.length,
      };
    });
  }, [monthlyItems, matrixMetric]);

  // Available daily trading months
  const availableDailyMonths = useMemo(() => {
    if (!history?.records) return [];
    const map: Record<string, number> = {};
    history.records.forEach((r) => {
      const ym = r.date.slice(0, 7);
      map[ym] = (map[ym] || 0) + 1;
    });
    return Object.entries(map).map(([key, count]) => {
      const [y, m] = key.split('-');
      const monthName = MONTH_NAMES[parseInt(m, 10) - 1] || m;
      return {
        key,
        label: `${monthName} ${y}`,
        count,
      };
    });
  }, [history?.records]);

  // Filtered daily trades
  const displayedDailyRecords = useMemo(() => {
    if (!history?.records) return [];
    if (selectedDailyMonth === 'all') return history.records;
    return history.records.filter((r) => r.date.startsWith(selectedDailyMonth));
  }, [history?.records, selectedDailyMonth]);

  // Heatmap color class calculation
  const getMatrixHeatClass = (val: number | undefined) => {
    if (val === undefined) return '';
    if (matrixMetric === 'sip') {
      if (val >= 25000) return 'ns-matrix-cell--pos-4';
      if (val >= 18000) return 'ns-matrix-cell--pos-3';
      if (val >= 13000) return 'ns-matrix-cell--pos-2';
      return 'ns-matrix-cell--pos-1';
    }
    if (matrixMetric === 'equity') {
      if (val >= 30000) return 'ns-matrix-cell--pos-4';
      if (val >= 15000) return 'ns-matrix-cell--pos-3';
      if (val > 0) return 'ns-matrix-cell--pos-1';
      if (val < -1000) return 'ns-matrix-cell--neg-3';
      return 'ns-matrix-cell--neg-1';
    }
    // aum
    if (val >= 60) return 'ns-matrix-cell--pos-4';
    if (val >= 45) return 'ns-matrix-cell--pos-3';
    if (val >= 35) return 'ns-matrix-cell--pos-2';
    return 'ns-matrix-cell--pos-1';
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Mutual Fund Flows & 5-Year SIP Trajectory"
      badge="AMFI & SEBI"
      badgeBg="var(--ns-surface-subtle, #f1f5f9)"
      badgeColor="var(--ns-text-secondary, #475569)"
      subtitle="Comprehensive Domestic Institutional Liquidity Desk • 70+ Months Historical Record (2020–2026)"
      icon={<TrendingUp size={16} />}
      iconStyle={{ background: 'var(--ns-surface-subtle, #f1f5f9)', color: 'var(--ns-text-secondary, #475569)', border: '1px solid var(--ns-border-default, #e2e8f0)' }}
      maxWidth="1140px"
      overlayClassName="ns-fiidii-modal-overlay"
      dialogClassName="ns-fiidii-modal"
      headerClassName="ns-fiidii-modal__header"
      bodyClassName="ns-fiidii-modal__body"
      footerClassName="ns-fiidii-modal__footer"
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
      headerActions={
        onSwitchToFiiDii ? (
          <button
            type="button"
            className="ns-fiidii-header-nav-btn"
            onClick={onSwitchToFiiDii}
            title="Switch to FII & DII Cash Market Desk"
          >
            <Landmark size={12} /> FII &amp; DII (14-Yr) &rarr;
          </button>
        ) : undefined
      }
      footerLeft={<span>Sources: AMFI (Association of Mutual Funds in India) &amp; SEBI Institutional Records.</span>}
      footerRight={<span>Macro Domestic Institutional Flow Engine</span>}
    >
      {/* Top 4 KPI Cards */}
      <div className="ns-mf-kpis ns-inst-kpi-grid">
        <InstitutionalKpiCard
          className="ns-mf-kpi-card"
          title="Monthly SIP Run-Rate"
          badge="AMFI Monthly"
          value={formatCr(summaryKpis.latestSip)}
          subItems={[
            `Daily Run-Rate: ~₹${Math.round(summaryKpis.latestSip / 30).toLocaleString('en-IN')} Cr/day`,
            `${(summaryKpis.latestAccounts / 100).toFixed(2)} Cr Folios`,
          ]}
        />
        <InstitutionalKpiCard
          className="ns-mf-kpi-card"
          title="Net Equity Inflows"
          badge="Pure Equity"
          value={formatCr(summaryKpis.latestEquity, true)}
          valueVariant={summaryKpis.latestEquity >= 0 ? 'pos' : 'neg'}
          subItems={[
            'Vs ₹29,315 Cr MoM (+30.6%)',
            <span key="disc" style={{ fontWeight: 600 }}>AMFI Disclosure</span>,
          ]}
        />
        <InstitutionalKpiCard
          className="ns-mf-kpi-card"
          title="5-Year Cumulative SIPs"
          badge="2020–2026"
          value={`₹${(summaryKpis.total5YrSip / 100000).toFixed(2)} Lakh Cr`}
          subItems={[
            '70+ Months Domestic SIP Moat',
            `${monthlyItems.length} Months Recorded`,
          ]}
        />
        <InstitutionalKpiCard
          className="ns-mf-kpi-card"
          title="Total Industry AUM"
          badge="All Assets"
          value={formatLakhCr(summaryKpis.latestAum)}
          subItems={[
            '2.8x Expansion from 2020 (₹27.8L Cr)',
            'All-Time High',
          ]}
        />
      </div>

      {/* Subtabs Navigation */}
      <SegmentedTabs<MfSubTab>
        className="ns-mf-subtabs"
        listClassName="ns-mf-subtab-buttons"
        buttonClassName="ns-mf-subtab-btn"
        badgeClassName="ns-fiidii-tab-count"
        tabs={[
          {
            id: 'chart',
            label: '5-Year SIP & Equity Growth (2020–2026)',
            icon: <BarChart2 size={13} />,
          },
          {
            id: 'category',
            label: 'Category-Wise Inflow Breakdown',
            icon: <Layers size={13} />,
          },
          {
            id: 'matrix',
            label: '5-Year Heatmap Matrix (2020–2026)',
            icon: <Grid size={13} />,
          },
          {
            id: 'daily',
            label: 'Daily SEBI Pure MF Trades',
            icon: <TableIcon size={13} />,
            badge: history?.records ? history.records.length : undefined,
          },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
        rightControls={
          activeTab === 'chart' ? (
            <div className="ns-mf-controls-strip">
              <FilterPillGroup<ChartMetric>
                buttonClassName="ns-mf-filter-btn"
                style={{ marginRight: 6 }}
                options={[
                  { value: 'both', label: 'SIP + Net Equity' },
                  { value: 'sip', label: 'SIP Only' },
                  { value: 'equity', label: 'Net Equity Only' },
                  { value: 'aum', label: 'AUM Expansion' },
                ]}
                selectedValue={chartMetric}
                onChange={setChartMetric}
              />
              <FilterPillGroup<ChartRange>
                buttonClassName="ns-mf-filter-btn"
                options={[
                  { value: 'all', label: 'All 5+ Yrs' },
                  { value: '3y', label: '3 Years' },
                  { value: 'recent', label: '2024–2026' },
                  { value: '1y', label: '1 Year' },
                ]}
                selectedValue={chartRange}
                onChange={setChartRange}
              />
            </div>
          ) : activeTab === 'matrix' ? (
            <div className="ns-mf-controls-strip">
              <FilterPillGroup<MatrixMetric>
                buttonClassName="ns-mf-filter-btn"
                options={[
                  { value: 'sip', label: 'Monthly SIP Inflows' },
                  { value: 'equity', label: 'Net Equity Inflow' },
                  { value: 'aum', label: 'Industry AUM (₹L Cr)' },
                ]}
                selectedValue={matrixMetric}
                onChange={setMatrixMetric}
              />
            </div>
          ) : activeTab === 'daily' && availableDailyMonths.length > 1 ? (
            <div className="ns-mf-controls-strip" style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <FilterPillGroup<string>
                buttonClassName="ns-mf-filter-btn"
                options={[
                  { value: 'all', label: `All (${history?.records?.length || 0})` },
                  ...availableDailyMonths.map((m) => ({
                    value: m.key,
                    label: `${m.label} (${m.count})`,
                  })),
                ]}
                selectedValue={selectedDailyMonth}
                onChange={setSelectedDailyMonth}
              />
            </div>
          ) : undefined
        }
      />

      {/* TAB 1: 5-Year SIP & Equity Growth Chart */}
      {activeTab === 'chart' && (
        <div className="ns-mf-chart-box">
          {/* Chart HUD */}
          <div className="ns-mf-chart-header">
            {activeHoverItem ? (
              <InstitutionalChartHud
                className="ns-mf-chart-hud"
                items={[
                  {
                    label: 'Month:',
                    value: `${activeHoverItem.monthName} ${activeHoverItem.year}`,
                    valueClassName: 'ns-mf-chart-hud__value',
                  },
                  {
                    label: 'Monthly SIP:',
                    value: formatCr(activeHoverItem.sipInflowCr),
                    valueClassName: 'ns-mf-chart-hud__value',
                  },
                  {
                    label: 'Net Equity Inflow:',
                    value: formatCr(activeHoverItem.netEquityInflowCr, true),
                    variant: activeHoverItem.netEquityInflowCr >= 0 ? 'pos' : 'neg',
                    valueClassName: 'ns-mf-chart-hud__value',
                  },
                  {
                    label: 'Total Industry AUM:',
                    value: formatLakhCr(activeHoverItem.totalIndustryAumLakhCr),
                    valueClassName: 'ns-mf-chart-hud__value',
                  },
                  ...(activeHoverItem.sipAccountsLakh
                    ? [
                        {
                          label: 'SIP Folios:',
                          value: `${(activeHoverItem.sipAccountsLakh / 100).toFixed(2)} Cr`,
                          valueClassName: 'ns-mf-chart-hud__value',
                        },
                      ]
                    : []),
                ]}
              />
            ) : (
              <div>Hover over bars to inspect monthly breakdown</div>
            )}

            <div style={{ fontSize: '11px', color: 'var(--ns-text-tertiary, #94a3b8)' }}>
              Displaying {filteredChartMonths.length} Months of Institutional Data
            </div>
          </div>

              {/* Chart Canvas with Y-Axis, Zero Line, and Dual-Zone Columns */}
              <div className="ns-mf-chart-canvas">
                <div className="ns-mf-chart-plot">
                  {/* Y-Axis scale benchmarks */}
                  <div className="ns-mf-chart-yaxis">
                    <span className="ns-mf-chart-yaxis-tick ns-mf-chart-yaxis-tick--top">
                      {yAxisTopLabel}
                    </span>
                    <span
                      className="ns-mf-chart-yaxis-tick ns-mf-chart-yaxis-tick--mid"
                      style={{ top: `${posZonePct / 2}%` }}
                    >
                      {yAxisMidLabel}
                    </span>
                    <span
                      className="ns-mf-chart-yaxis-tick ns-mf-chart-yaxis-tick--zero"
                      style={{ top: `${posZonePct}%` }}
                    >
                      ₹0
                    </span>
                    {hasNegativeEquity && yAxisNegLabel && (
                      <span className="ns-mf-chart-yaxis-tick ns-mf-chart-yaxis-tick--neg">
                        {yAxisNegLabel}
                      </span>
                    )}
                  </div>

                  {/* Horizontal Gridlines */}
                  <div
                    className="ns-mf-chart-gridline"
                    style={{ top: `${posZonePct / 2}%` }}
                  />
                  <div
                    className="ns-mf-chart-zero-line"
                    style={{ top: `${posZonePct}%` }}
                  />
                  {hasNegativeEquity && (
                    <div
                      className="ns-mf-chart-gridline ns-mf-chart-gridline--neg"
                      style={{ top: `${posZonePct + negZonePct / 2}%` }}
                    />
                  )}

                  {/* Chart Bars */}
                  <div className="ns-mf-bars-wrap">
                    {filteredChartMonths.map((item, idx) => {
                      const isHovered = activeHoverItem?.monthKey === item.monthKey;
                      const xLabel = getChartXAxisLabel(item, idx, filteredChartMonths.length);

                      const sipPct = Math.min(100, (item.sipInflowCr / maxPosVal) * 100);
                      const posEquityPct =
                        item.netEquityInflowCr > 0 ? Math.min(100, (item.netEquityInflowCr / maxPosVal) * 100) : 0;
                      const aumPct = Math.min(100, (item.totalIndustryAumLakhCr / maxPosVal) * 100);
                      const negEquityPct =
                        item.netEquityInflowCr < 0 && maxNegVal > 0
                          ? Math.min(100, (-item.netEquityInflowCr / maxNegVal) * 100)
                          : 0;

                      return (
                        <div
                          key={item.monthKey}
                          className={`ns-mf-bar-col ${isHovered ? 'is-hovered' : ''}`}
                          onMouseEnter={() => setHoveredMonth(item)}
                          onMouseLeave={() => setHoveredMonth(null)}
                        >
                          {/* Upper Zone (Positive >= 0) */}
                          <div
                            className="ns-mf-chart-zone ns-mf-chart-zone--pos"
                            style={{ height: `${posZonePct}%` }}
                          >
                            {chartMetric === 'both' && (
                              <div className="ns-mf-bar-subcols">
                                <div
                                  className="ns-mf-bar-col__fill-sip"
                                  style={{ height: `${Math.max(4, sipPct)}%` }}
                                  title={`SIP: ${formatCr(item.sipInflowCr)}`}
                                />
                                {item.netEquityInflowCr >= 0 ? (
                                  <div
                                    className="ns-mf-bar-col__fill-equity"
                                    style={{ height: `${Math.max(4, posEquityPct)}%` }}
                                    title={`Net Equity Inflow: ${formatCr(item.netEquityInflowCr, true)}`}
                                  />
                                ) : (
                                  <div className="ns-mf-bar-col__placeholder" />
                                )}
                              </div>
                            )}

                            {chartMetric === 'sip' && (
                              <div
                                className="ns-mf-bar-col__fill-sip"
                                style={{ height: `${Math.max(4, sipPct)}%` }}
                                title={`SIP: ${formatCr(item.sipInflowCr)}`}
                              />
                            )}

                            {chartMetric === 'equity' && item.netEquityInflowCr >= 0 && (
                              <div
                                className="ns-mf-bar-col__fill-equity"
                                style={{ height: `${Math.max(4, posEquityPct)}%` }}
                                title={`Net Equity Inflow: ${formatCr(item.netEquityInflowCr, true)}`}
                              />
                            )}

                            {chartMetric === 'aum' && (
                              <div
                                className="ns-mf-bar-col__fill-aum"
                                style={{ height: `${Math.max(4, aumPct)}%` }}
                                title={`AUM: ${formatLakhCr(item.totalIndustryAumLakhCr)}`}
                              />
                            )}
                          </div>

                          {/* Lower Zone (Negative Outflow < 0) */}
                          {hasNegativeEquity && (
                            <div
                              className="ns-mf-chart-zone ns-mf-chart-zone--neg"
                              style={{ height: `${negZonePct}%` }}
                            >
                              {chartMetric === 'both' && (
                                <div className="ns-mf-bar-subcols">
                                  <div className="ns-mf-bar-col__placeholder" />
                                  {item.netEquityInflowCr < 0 ? (
                                    <div
                                      className="ns-mf-bar-col__fill-equity ns-mf-bar-col__fill-equity--neg"
                                      style={{ height: `${Math.max(6, negEquityPct)}%` }}
                                      title={`Net Equity Outflow: ${formatCr(item.netEquityInflowCr, true)}`}
                                    />
                                  ) : (
                                    <div className="ns-mf-bar-col__placeholder" />
                                  )}
                                </div>
                              )}

                              {chartMetric === 'equity' && item.netEquityInflowCr < 0 && (
                                <div
                                  className="ns-mf-bar-col__fill-equity ns-mf-bar-col__fill-equity--neg"
                                  style={{ height: `${Math.max(6, negEquityPct)}%` }}
                                  title={`Net Equity Outflow: ${formatCr(item.netEquityInflowCr, true)}`}
                                />
                              )}
                            </div>
                          )}

                          {/* X-axis Month / Year Label */}
                          {xLabel && (
                            <span
                              className={`ns-mf-bar-col__label ${
                                item.month === 1 ? 'ns-mf-bar-col__label--year' : ''
                              }`}
                            >
                              {xLabel}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Chart Legend */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: '16px',
                  fontSize: '11px',
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  {(chartMetric === 'both' || chartMetric === 'sip') && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '10px', height: '10px', background: '#6366f1', borderRadius: '2px' }} />
                      <span>Monthly SIP Inflows (Predictable Domestic Cushion)</span>
                    </div>
                  )}
                  {(chartMetric === 'both' || chartMetric === 'equity') && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '10px', height: '10px', background: '#10b981', borderRadius: '2px' }} />
                        <span>Net Pure Equity Inflows (Positive)</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '10px', height: '10px', background: '#ef4444', borderRadius: '2px' }} />
                        <span>Net Equity Outflow (Negative / Below 0)</span>
                      </div>
                    </>
                  )}
                  {chartMetric === 'aum' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '10px', height: '10px', background: '#38bdf8', borderRadius: '2px' }} />
                      <span>Total Industry AUM Expansion</span>
                    </div>
                  )}
                </div>

                <span style={{ color: 'var(--ns-text-tertiary, #94a3b8)' }}>
                  Source: AMFI Official Industry Statistics
                </span>
              </div>
            </div>
          )}

          {/* TAB 2: Category Breakdown */}
          {activeTab === 'category' && (
            <div className="ns-mf-category-breakdown">
              <div style={{ fontSize: '11.5px', color: 'var(--ns-text-secondary, #64748b)', marginBottom: '4px' }}>
                💡 <strong>Where is domestic money rotating?</strong> Breakdown of monthly inflows into Small Cap, Mid Cap, Large Cap, Flexi Cap, and Sectoral/Thematic mutual funds:
              </div>

              {monthlyItems
                .filter((m) => m.categories)
                .slice(-12)
                .reverse()
                .map((item) => {
                  const cat = item.categories!;
                  const total =
                    cat.smallCap + cat.midCap + cat.largeCap + cat.flexiCap + cat.sectoralThematic + cat.otherEquity;
                  const smallPct = total !== 0 ? Math.max(0, (cat.smallCap / total) * 100) : 0;
                  const midPct = total !== 0 ? Math.max(0, (cat.midCap / total) * 100) : 0;
                  const largePct = total !== 0 ? Math.max(0, (cat.largeCap / total) * 100) : 0;
                  const flexiPct = total !== 0 ? Math.max(0, (cat.flexiCap / total) * 100) : 0;
                  const sectoralPct = total !== 0 ? Math.max(0, (cat.sectoralThematic / total) * 100) : 0;
                  const otherPct = total !== 0 ? Math.max(0, (cat.otherEquity / total) * 100) : 0;

                  return (
                    <div key={item.monthKey} className="ns-mf-cat-card">
                      <div className="ns-mf-cat-card__header">
                        <span className="ns-mf-cat-card__month">
                          📅 {item.monthName} {item.year}
                        </span>
                        <span className="ns-mf-cat-card__total">
                          Total Equity Flow: {formatCr(item.netEquityInflowCr, true)} (SIP: {formatCr(item.sipInflowCr)})
                        </span>
                      </div>

                      {/* Stacked Proportional Bar */}
                      <div className="ns-mf-cat-stack-bar">
                        <div className="ns-mf-cat-segment ns-mf-cat-segment--small" style={{ width: `${smallPct}%` }} title={`Small Cap: ${formatCr(cat.smallCap)} (${smallPct.toFixed(1)}%)`} />
                        <div className="ns-mf-cat-segment ns-mf-cat-segment--mid" style={{ width: `${midPct}%` }} title={`Mid Cap: ${formatCr(cat.midCap)} (${midPct.toFixed(1)}%)`} />
                        <div className="ns-mf-cat-segment ns-mf-cat-segment--large" style={{ width: `${largePct}%` }} title={`Large Cap: ${formatCr(cat.largeCap)} (${largePct.toFixed(1)}%)`} />
                        <div className="ns-mf-cat-segment ns-mf-cat-segment--flexi" style={{ width: `${flexiPct}%` }} title={`Flexi Cap: ${formatCr(cat.flexiCap)} (${flexiPct.toFixed(1)}%)`} />
                        <div className="ns-mf-cat-segment ns-mf-cat-segment--sectoral" style={{ width: `${sectoralPct}%` }} title={`Sectoral/Thematic: ${formatCr(cat.sectoralThematic)} (${sectoralPct.toFixed(1)}%)`} />
                        <div className="ns-mf-cat-segment ns-mf-cat-segment--other" style={{ width: `${otherPct}%` }} title={`Other Equity: ${formatCr(cat.otherEquity)}`} />
                      </div>

                      {/* Category Value Chips */}
                      <div className="ns-mf-cat-chips">
                        <div className="ns-mf-cat-chip">
                          <span className="ns-mf-cat-dot" style={{ background: '#ec4899' }} />
                          <span>Small Cap: <strong>{formatCr(cat.smallCap)}</strong> ({smallPct.toFixed(0)}%)</span>
                        </div>
                        <div className="ns-mf-cat-chip">
                          <span className="ns-mf-cat-dot" style={{ background: '#8b5cf6' }} />
                          <span>Mid Cap: <strong>{formatCr(cat.midCap)}</strong> ({midPct.toFixed(0)}%)</span>
                        </div>
                        <div className="ns-mf-cat-chip">
                          <span className="ns-mf-cat-dot" style={{ background: '#3b82f6' }} />
                          <span>Large Cap: <strong>{formatCr(cat.largeCap)}</strong> ({largePct.toFixed(0)}%)</span>
                        </div>
                        <div className="ns-mf-cat-chip">
                          <span className="ns-mf-cat-dot" style={{ background: '#10b981' }} />
                          <span>Flexi Cap: <strong>{formatCr(cat.flexiCap)}</strong> ({flexiPct.toFixed(0)}%)</span>
                        </div>
                        <div className="ns-mf-cat-chip">
                          <span className="ns-mf-cat-dot" style={{ background: '#f59e0b' }} />
                          <span>Sectoral / Thematic: <strong>{formatCr(cat.sectoralThematic)}</strong> ({sectoralPct.toFixed(0)}%)</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {/* TAB 3: 5-Year Monthly Heatmap Matrix */}
          {activeTab === 'matrix' && (
            <div className="ns-mf-matrix-container">
              <div style={{ fontSize: '11px', color: 'var(--ns-text-tertiary, #94a3b8)', marginBottom: '8px' }}>
                Showing <strong>{matrixMetric === 'sip' ? 'Monthly SIP Inflow (₹ Cr)' : matrixMetric === 'equity' ? 'Net Equity Inflow (₹ Cr)' : 'Total Industry AUM (₹ Lakh Cr)'}</strong> across all calendar months:
              </div>

              <table className="ns-mf-matrix-table">
                <thead>
                  <tr>
                    <th>Year</th>
                    {MONTH_NAMES.map((m) => (
                      <th key={m}>{m}</th>
                    ))}
                    <th>Full Year</th>
                  </tr>
                </thead>
                <tbody>
                  {matrixData.map(({ year, months, total }) => (
                    <tr key={year}>
                      <td style={{ fontWeight: 700 }}>{year}</td>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((mNum) => {
                        const val = months[mNum];
                        const formatted =
                          val !== undefined
                            ? matrixMetric === 'aum'
                              ? `₹${val.toFixed(1)}L`
                              : `${val > 0 ? '+' : ''}${Math.round(val).toLocaleString('en-IN')}`
                            : '—';

                        return (
                          <td
                            key={mNum}
                            className={getMatrixHeatClass(val)}
                            title={`${MONTH_NAMES[mNum - 1]} ${year}: ${val !== undefined ? formatCr(val) : 'N/A'}`}
                          >
                            {formatted}
                          </td>
                        );
                      })}
                      <td className="ns-mf-matrix-col-total">
                        {matrixMetric === 'aum'
                          ? `₹${total.toFixed(2)}L Cr`
                          : `${total > 0 ? '+' : ''}${Math.round(total).toLocaleString('en-IN')}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 4: Daily SEBI Pure Mutual Fund Trades */}
          {activeTab === 'daily' && (
            <div className="ns-mf-table-wrap">
              {displayedDailyRecords.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--ns-text-tertiary, #94a3b8)' }}>
                  No daily records found.
                </div>
              ) : (
                <table className="ns-mf-daily-table">
                  <thead>
                    <tr>
                      <th>Trading Date</th>
                      <th>Equity Buy (₹ Cr)</th>
                      <th>Equity Sell (₹ Cr)</th>
                      <th>Pure Equity Net</th>
                      <th>Debt Net (₹ Cr)</th>
                      <th>Total MF Net</th>
                      <th>NSE DII Net (₹ Cr)</th>
                      <th>MF Share of DII</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedDailyRecords.map((row) => {
                      const isLatest = row.date === latest?.date;
                      return (
                        <tr key={row.date} style={isLatest ? { background: 'rgba(99, 102, 241, 0.05)', fontWeight: 600 } : undefined}>
                          <td>
                            <strong>{row.date}</strong>
                            {isLatest && (
                              <span className="ns-fiidii-latest-badge" style={{ marginLeft: 6 }}>
                                LATEST
                              </span>
                            )}
                          </td>
                          <td>{formatCr(row.equityBuy).replace(' Cr', '')}</td>
                          <td>{formatCr(row.equitySell).replace(' Cr', '')}</td>
                          <td className={row.equityNet >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                            {formatCr(row.equityNet, true)}
                          </td>
                          <td className={row.debtNet >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                            {formatCr(row.debtNet, true)}
                          </td>
                          <td style={{ fontWeight: 700 }} className={row.totalNet >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                            {formatCr(row.totalNet, true)}
                          </td>
                          <td>
                            {row.diiEquityNet !== undefined ? formatCr(row.diiEquityNet, true) : '—'}
                          </td>
                          <td>
                            {row.mfShareOfDiiPct !== undefined ? (
                              <span className="ns-mf-badge-share">
                                {row.mfShareOfDiiPct}% of DII
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
    </ModalShell>
  );
};

export default MutualFundFlowModal;
